import { REDIRECT_TARGETS } from '@shared/constants/redirect-targets';

/**
 * Validates if a redirect URL is safe based on whitelisted targets.
 * Allows any path/hash on whitelisted hostnames, but enforces HTTPS.
 * @param redirectUrl - The URL to validate (can be full URL or hostname)
 * @returns true if the URL is safe, false otherwise
 */
export function isSafeRedirectUrl(redirectUrl: string): boolean {
	let parsedRedirect: URL;

	try {
		// Add https:// if missing for parsing
		const urlToparse = redirectUrl.startsWith('http') ? redirectUrl : `https://${redirectUrl}`;
		parsedRedirect = new URL(urlToparse);
	} catch {
		return false;
	}

	// Enforce HTTPS only
	if (parsedRedirect.protocol !== 'https:') {
		return false;
	}

	// Check if hostname matches any whitelisted target
	return REDIRECT_TARGETS.some((target) => {
		try {
			const targetUrlToParse = target.url.startsWith('http') ? target.url : `https://${target.url}`;
			const targetHost = new URL(targetUrlToParse).hostname;
			// Compare only hostnames, path/hash is irrelevant
			return parsedRedirect.hostname === targetHost;
		} catch {
			return false;
		}
	});
}

/**
 * Extracts and normalizes the hostname from a URL
 * @param url - The URL string
 * @returns The hostname, or empty string if invalid
 */
export function getHostnameFromUrl(url: string): string {
	try {
		const urlToparse = url.startsWith('http') ? url : `https://${url}`;
		return new URL(urlToparse).hostname;
	} catch {
		return '';
	}
}
